import { concepts, lessons, words } from '../data/foundations'
import type { Concept, Lesson, Word } from '../types/content'
import type { Progress } from '../types/progress'

const orderById = new Map(concepts.map((concept) => [concept.id, concept.order]))
const lessonsByConcept = new Map(concepts.map((concept) => [concept.id, lessons.filter((lesson) => lesson.conceptId === concept.id)]))
export const currentConcept = (progress: Progress) => concepts.find((concept) => concept.id === progress.currentConceptId) ?? concepts[0]
export const lessonsFor = (conceptId: string) => lessonsByConcept.get(conceptId) ?? []
export const isConceptComplete = (conceptId: string, progress: Progress) => {
  const list = lessonsFor(conceptId)
  return list.length > 0 && list.every((lesson) => progress.completedLessons.includes(lesson.id))
}
export const isConceptUnlocked = (concept: Concept, progress: Progress) => concept.order <= currentConcept(progress).order
export const nextLessonFor = (conceptId: string, progress: Progress) => {
  const list = lessonsFor(conceptId)
  return list.find((lesson) => !progress.completedLessons.includes(lesson.id)) ?? list[0]
}
export const canDecodeAt = (word: Word, order: number) => !word.isPseudoWord && !word.teacherSupported && word.decodableAfterConcepts.every((id) => (orderById.get(id) ?? Infinity) <= order)
// Availability is not evidence of mastery.
export const unlockedWords = (progress: Progress) => words.filter((word) => canDecodeAt(word, currentConcept(progress).order))
export const lessonLabels: Record<Lesson['type'], string> = {
  hear: '听一听', recognition: '听音找字', 'sound-hunt': '找一找', blend: '连起来读',
  segment: '拆开声音', decode: '读一读', encode: '听音拼词', 'word-challenge': '单词挑战', review: '再试一次',
}
export function shuffled<T>(items: readonly T[], random: () => number = Math.random): T[] {
  const result = [...items]
  for (let index = result.length - 1; index > 0; index--) {
    const swap = Math.floor(random() * (index + 1))
    ;[result[index], result[swap]] = [result[swap], result[index]]
  }
  return result
}
export function completeLessonProgress(current: Progress, lesson: Lesson, date: string): Progress {
  if (current.completedLessons.includes(lesson.id)) return current
  const completedLessons = [...current.completedLessons, lesson.id]
  const updated = { ...current, completedLessons }
  let frontier = Math.max(0, concepts.findIndex((concept) => concept.id === current.currentConceptId))
  while (frontier < concepts.length - 1 && isConceptComplete(concepts[frontier].id, updated)) frontier++
  const dailyRecords = current.dailyRecords.some((record) => record.date === date)
    ? current.dailyRecords.map((record) => record.date === date ? { ...record, lessons: record.lessons + 1 } : record)
    : [...current.dailyRecords, { date, seconds: 0, lessons: 1 }]
  return {
    ...updated, currentConceptId: concepts[frontier].id, stars: current.stars + 1, plantLevel: current.plantLevel + 1,
    unlockedItems: isConceptComplete(lesson.conceptId, updated) ? Array.from(new Set([...current.unlockedItems, `stage_${lesson.stage}_seed`])) : current.unlockedItems,
    dailyRecords: dailyRecords.slice(-14),
  }
}
